<!--
  Destructive broadcast control (directive section 17).

  The interaction is deliberately different from a normal button:
    1. it reads as dangerous before it is pressed;
    2. the first press arms it rather than firing it;
    3. an armed control cannot fire for `armDelayMs`, which defeats an accidental
       touch on a tablet in a studio;
    4. where the action requires one, a reason must be typed before it can fire;
    5. it disarms itself, so a control left armed does not stay armed.

  Actions listed in REASON_REQUIRED_ACTIONS must pass `requireReason`.
-->
<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue';

const props = withDefaults(
  defineProps<{
    label: string;
    confirmLabel?: string;
    cancelLabel?: string;
    waitLabel?: string;
    requireReason?: boolean;
    reasonLabel?: string;
    armDelayMs?: number;
    autoDisarmMs?: number;
    disabled?: boolean;
    loading?: boolean;
  }>(),
  {
    confirmLabel: 'Confirm',
    cancelLabel: 'Cancel',
    waitLabel: 'Wait…',
    requireReason: false,
    reasonLabel: 'Reason (recorded in the audit log)',
    armDelayMs: 600,
    autoDisarmMs: 8000,
    disabled: false,
    loading: false,
  },
);

const emit = defineEmits<{ confirm: [reason: string | undefined] }>();

const armed = ref(false);
const ready = ref(false);
const reason = ref('');
let readyTimer: ReturnType<typeof setTimeout> | undefined;
let disarmTimer: ReturnType<typeof setTimeout> | undefined;

const canConfirm = computed(
  () => ready.value && (!props.requireReason || reason.value.trim().length >= 3),
);

function clearTimers() {
  if (readyTimer) clearTimeout(readyTimer);
  if (disarmTimer) clearTimeout(disarmTimer);
  readyTimer = undefined;
  disarmTimer = undefined;
}

function arm() {
  armed.value = true;
  ready.value = false;
  clearTimers();
  readyTimer = setTimeout(() => {
    ready.value = true;
  }, props.armDelayMs);
  disarmTimer = setTimeout(disarm, props.autoDisarmMs);
}

function disarm() {
  armed.value = false;
  ready.value = false;
  reason.value = '';
  clearTimers();
}

function confirm() {
  if (!canConfirm.value) return;
  const value = props.requireReason ? reason.value.trim() : undefined;
  disarm();
  emit('confirm', value);
}

onBeforeUnmount(clearTimers);
</script>

<template>
  <div class="shio-danger">
    <button
      v-if="!armed"
      class="shio-btn shio-btn--danger"
      type="button"
      :disabled="disabled || loading"
      @click="arm"
    >
      <slot name="icon" />
      {{ label }}
    </button>

    <div v-else class="shio-danger__confirm" role="group" :aria-label="`${confirmLabel}: ${label}`">
      <p class="shio-body-sm shio-danger__prompt">{{ label }}</p>

      <label v-if="requireReason" class="shio-field">
        <span class="shio-label">{{ reasonLabel }}</span>
        <input
          v-model="reason"
          class="shio-input"
          type="text"
          autofocus
          minlength="3"
          :aria-invalid="reason.trim().length > 0 && reason.trim().length < 3"
        />
      </label>

      <div class="shio-danger__actions">
        <button class="shio-btn shio-btn--ghost shio-btn--sm" type="button" @click="disarm">
          {{ cancelLabel }}
        </button>
        <button
          class="shio-btn shio-btn--danger shio-btn--sm"
          type="button"
          data-armed="true"
          :disabled="!canConfirm || loading"
          :aria-disabled="!canConfirm"
          @click="confirm"
        >
          {{ ready ? confirmLabel : waitLabel }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.shio-danger__confirm {
  display: flex;
  flex-direction: column;
  gap: var(--shio-space-2);
  padding: var(--shio-space-3);
  background: hsl(var(--shio-status-danger) / 0.08);
  border: var(--shio-border-width) solid hsl(var(--shio-status-danger) / 0.55);
  border-radius: var(--shio-radius-md);
}

.shio-danger__prompt {
  font-weight: 600;
  color: hsl(var(--shio-status-danger-strong));
  margin: 0;
}

.shio-danger__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--shio-space-2);
}
</style>
